import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import RateReviewIcon from '@mui/icons-material/RateReview'

import { servicesApi } from '../../api/endpoints'
import { forgetTeamBrowser, isTeamBrowser, rememberTeamBrowser } from '../../api/client'
import { useLocale } from '../../i18n/LocaleProvider'

const NAME_KEY = 'lakeside.reviewer'

/**
 * A floating "leave a note" button for the people reviewing the site before launch.
 *
 * <p>The owner needs to comment on pages, and the alternative is a chat thread of screenshots
 * where nobody can tell which page "the text is wrong" refers to. This captures the route and
 * the language on screen alongside the note, so feedback arrives already located — and a
 * complaint about wording is language-specific, which is why the locale travels too.
 *
 * <p>Who sees it: everyone when {@code VITE_REVIEW_MODE} is on (a staging build), and otherwise
 * only a browser someone from the team has signed in to the console from. That is the
 * production setting. Real guests arriving from Google or Facebook never see a button asking
 * them to leave notes, and the three people who use it keep it without switching anything.
 * It gates nothing secure: the server still checks the team code on every note.
 *
 * <p>A team phone that never signs in to the console turns it on with a link:
 * {@code https://lomasaaret.mnjlabs.com/?review=on} (and off again with {@code ?review=off}).
 * The parameter is removed from the address straight away, so a page shared afterwards does
 * not carry it to a guest.
 *
 * <p>The reviewer's name is remembered locally after the first note. Asking a non-technical
 * owner to type their name every time is how you stop getting notes.
 */
type Requester = 'LENA' | 'MICHAEL' | 'NAUM' | 'OTHER'

const REQUESTERS: { value: Requester; label: string }[] = [
  { value: 'LENA', label: 'Lena (owner)' },
  { value: 'MICHAEL', label: 'Michael' },
  { value: 'NAUM', label: 'Naum' },
  { value: 'OTHER', label: 'Someone else' },
]

const WHO_KEY = 'lakeside.review.who'
const TOKEN_KEY = 'lakeside.review.token'

function remembered(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function remember(key: string, value: string): void {
  try {
    if (value) window.localStorage.setItem(key, value)
  } catch {
    // A private window that forgets the answer is survivable; failing to send is not.
  }
}

export function ReviewNoteWidget() {
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()
  const [teamBrowser, setTeamBrowser] = useState(isTeamBrowser)
  const enabled = import.meta.env.VITE_REVIEW_MODE === 'true' || teamBrowser

  // ?review=on / ?review=off - the switch for browsers that never sign in to the console.
  useEffect(() => {
    const params = new URLSearchParams(search)
    const review = params.get('review')
    if (review !== 'on' && review !== 'off') return
    if (review === 'on') {
      rememberTeamBrowser()
    } else {
      forgetTeamBrowser()
    }
    setTeamBrowser(review === 'on')
    params.delete('review')
    const rest = params.toString()
    navigate({ pathname, search: rest ? `?${rest}` : '', hash }, { replace: true })
  }, [search, pathname, hash, navigate])
  const { locale } = useLocale()

  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [author, setAuthor] = useState(() => remembered(NAME_KEY))
  const [requester, setRequester] = useState<Requester>(
    () => (remembered(WHO_KEY) as Requester) || 'OTHER',
  )
  // Typed once and then remembered. Asking a non-technical owner to re-enter a secret every
  // time she spots a wrong price is how you stop hearing about wrong prices.
  const [token, setToken] = useState(() => remembered(TOKEN_KEY))

  const submit = useMutation({
    mutationFn: () =>
      servicesApi.addSiteNote({
        pagePath: pathname,
        locale,
        note: note.trim(),
        authorName: author.trim() || undefined,
        requester,
        teamToken: token.trim() || undefined,
      }),
    onSuccess: () => {
      remember(NAME_KEY, author.trim())
      remember(WHO_KEY, requester)
      remember(TOKEN_KEY, token.trim())
      setNote('')
      window.setTimeout(() => {
        setOpen(false)
        submit.reset()
      }, 1400)
    },
  })

  if (!enabled) {
    return null
  }

  return (
    <>
      {/*
        Labelled, not an icon alone. A bare brown square under the scroll arrows was read as
        decoration - the team reported the button "missing" while it was on screen.
      */}
      <Fab
        variant="extended"
        color="secondary"
        onClick={() => setOpen(true)}
        aria-label="Leave a note about this page"
        sx={{
          position: 'fixed',
          bottom: 24,
          // Logical property, so it sits on the correct edge in Hebrew too.
          insetInlineEnd: 24,
          zIndex: 1300,
          gap: 1,
          fontWeight: 700,
          textTransform: 'none',
          boxShadow: 6,
        }}
      >
        <RateReviewIcon />
        Feedback
      </Fab>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Leave a note about this page</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Page
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {pathname} · {locale}
              </Typography>
            </Box>

            <TextField
              select
              label="Who is asking?"
              size="small"
              value={requester}
              onChange={(event) => setRequester(event.target.value as Requester)}
            >
              {REQUESTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {requester === 'OTHER' && (
              <TextField
                label="Your name"
                size="small"
                value={author}
                onChange={(event) => setAuthor(event.target.value)}
                placeholder="So we know who to ask"
              />
            )}

            <TextField
              label="Team code"
              size="small"
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              helperText={
                token.trim()
                  ? 'Remembered on this device. Requests sent with it are acted on directly.'
                  : 'Without it the note is saved as anonymous, and the name above is only a claim.'
              }
            />

            <TextField
              label="What should change?"
              multiline
              minRows={4}
              autoFocus
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="The price for breakfast is wrong, this photo is of the wrong cottage, ..."
            />

            {submit.isSuccess && (
              <Alert severity="success">
                Sent. Lena, Michael and Naum have all been notified, and the answer comes back
                on this request.
              </Alert>
            )}
            {submit.isError && <Alert severity="error">Could not save that. Please try again.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={!note.trim() || submit.isPending || submit.isSuccess}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Sending' : 'Send note'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
